import type { SkillListScope, SkillSummary } from "@/shared/domain/skills";

/**
 * The Skills tab's scope toggle: every visible skill, or one of the two
 * server-side {@link SkillListScope} partitions.
 */
export type SkillsScope = "all" | SkillListScope;

/**
 * Narrow the caller's visible skills to `scope`.
 *
 * Mirrors the list endpoint's `scope` partitions (`skills_repo.py`
 * `list_skills_for_org`) so the page can load the unscoped list once and still
 * count every scope:
 * - `"personal"`: skills `userId` wrote or has subscribed to.
 * - `"org"`: org-shared skills `userId` did not write, subscribed or not.
 * - `"all"`: every skill passed in.
 *
 * A teammate's org-shared skill the caller subscribed to is in both
 * partitions, so the two counts can add up to more than `"all"`.
 */
export function skillsInScope(
  skills: SkillSummary[],
  scope: SkillsScope,
  userId: string,
): SkillSummary[] {
  switch (scope) {
    case "all":
      return skills;
    case "personal":
      return skills.filter(
        (s) => s.skill.created_by === userId || s.subscription !== null,
      );
    case "org":
      return skills.filter(
        (s) =>
          s.skill.accessibility !== "private" && s.skill.created_by !== userId,
      );
  }
}

/** How many of `skills` each {@link SkillsScope} holds, for the toggle's counts. */
export function countSkillsByScope(
  skills: SkillSummary[],
  userId: string,
): Record<SkillsScope, number> {
  return {
    all: skills.length,
    personal: skillsInScope(skills, "personal", userId).length,
    org: skillsInScope(skills, "org", userId).length,
  };
}
